import { Dialog } from '@capacitor/dialog'
import { useTourStore } from '@/stores/tour'
import { biometric } from '@/services/biometric'
import { mock } from '@/services'
import { useAuthHelpers } from '@/composables/authHelpers'
import { useAppStore } from '@/stores'
import versionTypes from '@/constants/versionTypes'
import mockType from '@/constants/mockType'

export function useLaunchHelpers() {
    const loadHomepage = async (router) => {
        const appStore = useAppStore()
        const dashboardStore = useDashboardStore()
        const spacesStore = useSpacesStore()
        const versionStore = useVersionStore()
        const identityStore = useIdentityStore()
        const { snackbar } = useSnackbars()
        const loadingOverlays = useLoadingOverlays()

        const authHelpers = useAuthHelpers()

        const { version } = storeToRefs(versionStore)

        let contentPromise, targetRoute
        if (version.value.type === versionTypes.nextGen) {
            const mockingOverrides = {
                [mockType.more]: { promise: Promise.resolve(), targetRoute: { name: 'next-gen.more' } },
                [mockType.news]: { promise: Promise.resolve(), targetRoute: { name: 'next-gen.news' } },
                [mockType.notifications]: { promise: Promise.resolve(), targetRoute: { name: 'next-gen.notifications' } },
            }

            // If mocking set the screen accordingly
            const mockingOverride = mockingOverrides[mock.type()]
            if (mockingOverride) {
                contentPromise = mockingOverride.promise
                targetRoute = mockingOverride.targetRoute
            } else {
                targetRoute = { name: 'next-gen.spaces.me' }
                contentPromise = spacesStore.fetch('me')
            }
        } else {
            targetRoute = { name: 'dashboard' }
            contentPromise = dashboardStore.load()
        }

        return contentPromise
            .then(async () => {
                const tourStore = useTourStore()

                const { launchAnimation } = useAnimations()
                const { snackbar } = useSnackbars()

                if (!version.value.tour_enabled && version.value.biometrics_enabled && await biometric.canRequestBiometry()) {
                    await biometric.setPreviouslyRequested()
                    const biometryType = await biometric.biometryType()
                    Dialog.confirm({
                        title: `Enable ${ biometryType }?`,
                        message: `Would you like to enable ${ biometryType }?`,
                        okButtonTitle: 'Enable',
                        cancelButtonTitle: 'Dismiss',
                    })
                        .then(async ({ value }) => {
                            if (value) {
                                try {
                                    await biometric.authenticate().then(() => {
                                        biometric.setEnabled(true)
                                    }).catch(() => {
                                        biometric.setEnabled(false)
                                    })
                                } catch (error) {
                                    await biometric.setEnabled(false)
                                    snackbar({
                                        message: `Unable to enable ${ biometryType }`,
                                        type: 'error',
                                    })
                                }
                            }
                        })
                }

                // Load the identity store
                if (version.value.identification_settings) {
                    identityStore.load()
                }

                // Disable animation when mocking
                const mocking = mock.mocking()
                const animation = !mocking ? launchAnimation : null

                const shouldLoadTour = await tourStore.shouldLoad()

                if (!mocking) {
                    if (shouldLoadTour) {
                        await tourStore.load()
                    } else {
                        tourStore.storeCompleteTourStatus()
                    }
                }

                router.replace(targetRoute, animation)
            })
            .catch((e) => {
                if (authHelpers.isAuthenticationException(e)) {
                    authHelpers.handleAuthenticationException(
                        e.response.data,
                        router,
                        () => {
                            // Try loading the dashboard again
                            loadingOverlays.push()
                            return loadHomepage(router)
                                .catch(() => {
                                    // If it fails again, show a snackbar error
                                    // We're not sure what state the app is in, so we restart
                                    snackbar({
                                        title: 'Unable to load homepage',
                                        message: 'Please try again later',
                                        type: 'error',
                                        indefinite: true,
                                        onClose: () => {
                                            appStore.restart()
                                        },
                                    })
                                })
                                .finally(() => {
                                    loadingOverlays.pop()
                                })
                        },
                        null,
                        false,
                        true,
                    )
                    return
                }
                throw e
            })
    }

    return {
        loadHomepage,
    }
}
